import { CourseClass } from '@api';
import { normalize } from 'normalizr';
import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { Persistor } from 'redux-persist/es/types';
import { PersistGate } from 'redux-persist/integration/react';
import { ATTR_DATA_CID } from 'src/js/constants/Utils';
import { HTMLMarker } from 'src/js/model/common/utils';
import { ADOBE_REACT_ID, AdobeAnalyticsCheckoutStartTrack } from 'src/js/services/AdobeAnalyticsService';
import { initAnalyticsEvent } from 'src/js/services/CommonAnalyticsService';
import { ConfigConstants } from '../../config/ConfigConstants';
import { bugsnagClient, ErrorBoundary } from '../../constants/Bugsnag';
import { localForage } from '../../constants/LocalForage';
import { ClassesListSchema } from '../../NormalizeSchema';
import { IshState } from '../../services/IshState';
import { whenReady } from '../../services/jq';
import { Level, Logger, LogMessage } from '../../services/Logger';
import { Actions } from '../../web/actions/Actions';
import { htmlProps2CourseClass } from '../../web/services/CourseClassService';
import { HTMLMarkers } from '../services/HTMLMarker';
import * as HtmlDataService from './HtmlUtils';

export class Bootstrap {
  private components: { [key: string]: HTMLMarker } = {};

  private roots: Map<HTMLElement, Root> = new Map();

  private store: Store<IshState>;

  private persistor: Persistor;

  constructor(store: Store<IshState>, persistor: Persistor) {
    this.store = store;
    this.persistor = persistor;
  }

  public register = (marker: HTMLMarker): Bootstrap => {
    this.components[marker.id] = marker;
    return this;
  };

  /**
   * onEven parameter needs to have a possibility to start bootstrap from tests
   */
  public start = (onEvent = true): Bootstrap => {
    onEvent ? whenReady(() => this.bootstrap()) : this.bootstrap();
    return this;
  };

  private renderMarker = (container: HTMLElement, marker: HTMLMarker) => {
    try {
      if (container.childElementCount > 1 || (container.firstElementChild && container.firstElementChild.hasAttribute('data-reactroot'))) {
        Logger.log(new LogMessage(Level.DEBUG, `Container ${marker.id} contains already mounted react module or more than one children element`));
        return;
      }
      const realProps = HtmlDataService.parse(container, marker);

      const root = this.roots.has(container) ? this.roots.get(container) : createRoot(container);

      if (!this.roots.has(container)) {
        this.roots.set(container, root);
      }

      root.render(
        <ErrorBoundary>
          <Provider store={this.store}>
            <PersistGate loading={null} persistor={this.persistor}>
              <marker.component {...realProps}/>
            </PersistGate>
          </Provider>
        </ErrorBoundary>
      );
    } catch (e) {
      bugsnagClient.notify(e);
      Logger.log(new LogMessage(Level.ERROR, `Component with cid:${marker.id} cannot be instantiated.`, [e]));
      Logger.log(new LogMessage(Level.INFO, 'State', [this.store.getState()]));
      Logger.log(new LogMessage(Level.INFO, 'App version', [ConfigConstants.APP_VERSION]));

      localForage.clear().then(() => {
        Logger.log(new LogMessage(Level.INFO, 'Local storage cleared'));
      }).catch(e => {
        console.error(e);
      });
    }
  };

  /**
   * This function convert loaded html properties to correspondent object and add this object to the react store
   */
  private addToStore = (marker: HTMLMarker, props: { [key: string]: any }, dispatch) => {
    switch (marker) {
      case HTMLMarkers.ENROL_BUTTON:
        const courseClass: CourseClass = htmlProps2CourseClass(props);
        if (courseClass.course) {
          dispatch({
            type: Actions.PutClassToStore,
            payload: normalize(courseClass, ClassesListSchema),
          });
        }
    }
  };

  private onLinkClick = (type, code) => {
    initAnalyticsEvent({
        ecAction: 'linkClick',
        type,
        code,
      },
      this.store.getState());
  };


  private bootstrap = (): void => {
    try {
      const classLinks = document.querySelectorAll(`a[href*="${window.location.origin}/class/"]`) as any;
      const productLinks = document.querySelectorAll(`a[href*="${window.location.origin}/product/"]`) as any;

      classLinks.forEach(l => {
        const codeMatch = l.getAttribute('href').match(/[^/]+$/);
        const code = codeMatch ? codeMatch[0] : '';
        l.onclick = () => this.onLinkClick('class', code);
      });

      productLinks.forEach(l => {
        const codeMatch = l.getAttribute('href').match(/[^/]+$/);
        const code = codeMatch ? codeMatch[0] : '';
        l.onclick = () => this.onLinkClick('product', code);
      });

      Object.keys(this.components).forEach(cid => {
        const marker: HTMLMarker = this.components[cid];
        const containers = document.querySelectorAll(`[${ATTR_DATA_CID}='${cid}']${marker.skipOn || ''}`) as any;
        containers.forEach(container => {
          this.renderMarker(container, marker);
        });
      });

      if (!this.roots.has(document.getElementById(ADOBE_REACT_ID))) {
        const shadowRoot = document.createElement('data');
        shadowRoot.id = ADOBE_REACT_ID;
        document.body?.append(shadowRoot);
        this.roots.set(shadowRoot, createRoot(shadowRoot));
      }

      this.roots.get(document.getElementById(ADOBE_REACT_ID)).render(
        <ErrorBoundary>
          <Provider store={this.store}>
            <PersistGate loading={null} persistor={this.persistor}>
              <AdobeAnalyticsCheckoutStartTrack />
            </PersistGate>
          </Provider>
        </ErrorBoundary>
      )
    } catch (e) {
      bugsnagClient.notify(e);
      Logger.log(new LogMessage(Level.ERROR, 'Unhandled error', e));
    }
  };
}
